import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Papa from "papaparse";
import {
  DownloadSimple,
  ArrowClockwise,
  Info,
} from "@phosphor-icons/react";

import { SearchHero } from "@/components/gbp/SearchHero";
import { ResultsTable } from "@/components/gbp/ResultsTable";
import { ScanProgress } from "@/components/gbp/ScanProgress";
import { RecentSearches } from "@/components/gbp/RecentSearches";
import { OutreachDrawer } from "@/components/gbp/OutreachDrawer";
import { LeadNotesDialog } from "@/components/gbp/LeadNotesDialog";
import { TopNav, bumpCallbackAgenda } from "@/components/gbp/TopNav";
import { createScan, getScan, getRecentScans, fetchLeads } from "@/lib/api";

export default function Home() {
  const [scanId, setScanId] = useState(null);
  const [scan, setScan] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftTarget, setDraftTarget] = useState(null);
  const [leadTarget, setLeadTarget] = useState(null);
  const [leads, setLeads] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const pollRef = useRef(null);

  // Open a scan referenced by ?scan= query param (from /history navigation).
  useEffect(() => {
    const requested = searchParams.get("scan");
    if (requested && requested !== scanId) {
      setLoading(true);
      setScan(null);
      setScanId(requested);
      // Clear the query string so a manual "New scan" click doesn't reload it.
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refreshRecent = useCallback(async () => {
    try {
      const items = await getRecentScans(10);
      setRecent(items);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  // Poll while scan is active
  useEffect(() => {
    if (!scanId) return undefined;

    const tick = async () => {
      try {
        const doc = await getScan(scanId);
        setScan(doc);
        if (doc.status === "completed" || doc.status === "failed") {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setLoading(false);
          if (doc.status === "completed") {
            toast.success(`Scan complete · ${doc.total} businesses`);
          } else {
            toast.error(doc.error || "Scan failed");
          }
          refreshRecent();
        }
      } catch (e) {
        // keep polling
      }
    };

    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [scanId, refreshRecent]);

  // Fetch leads whenever the scan's results change
  useEffect(() => {
    const placeIds = (scan?.results || [])
      .map((r) => r.place_id)
      .filter(Boolean);
    if (!placeIds.length) {
      setLeads({});
      return;
    }
    let cancelled = false;
    fetchLeads(placeIds)
      .then((items) => {
        if (!cancelled) setLeads(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scan?.results?.length, scan?.id]);

  const startScan = async ({ niche, area, cap }) => {
    setLoading(true);
    setScan(null);
    try {
      const { scan_id } = await createScan({ niche, area, cap });
      setScanId(scan_id);
      toast.message("Scan started", {
        description: `${niche} · ${area}`,
      });
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || "Could not start scan");
    }
  };

  const pickRecent = async (item) => {
    setLoading(true);
    setScanId(item.id);
    try {
      const doc = await getScan(item.id);
      setScan(doc);
      if (doc.status === "completed" || doc.status === "failed") {
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      toast.error("Could not load that scan");
    }
  };

  const rows = scan?.results || [];
  const canExport = scan?.status === "completed" && rows.length > 0;

  const downloadCsv = () => {
    if (!rows.length) return;
    const csv = Papa.unparse(
      rows.map((r) => {
        const lead = leads[r.place_id] || {};
        return {
          business: r.title,
          category: r.category || "",
          address: r.address || "",
          phone: r.phone || "",
          website: r.website || "",
          rating: r.rating ?? "",
          reviews: r.reviews ?? "",
          total_posts: r.total_posts ?? 0,
          posts_this_month: r.posts_this_month ?? 0,
          posts_last_month: r.posts_last_month ?? 0,
          posts_truncated: r.posts_truncated ? "yes" : "no",
          first_post: r.first_post_date || "",
          last_post: r.last_post_date || "",
          last_post_text: r.last_post_relative || "",
          posts_last_3_months: r.posts_last_3_months ?? 0,
          posts_per_month: r.posts_per_month ?? 0,
          activity: r.activity_status || "none",
          gbp_url: r.gbp_url || "",
          ai_visibility: r.ai_visibility || "",
          google_ai_position: r.ai_position || "",
          lead_status: lead.status || "not_contacted",
          remark: lead.remark || "",
          callback_at: lead.callback_at || "",
        };
      }),
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gbp-scan-${(scanId || "").slice(0, 8)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const resetScan = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setScanId(null);
    setScan(null);
    setLoading(false);
  };

  const active = scan?.status && scan.status !== "completed" && scan.status !== "failed";

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      <TopNav />
      <SearchHero onScan={startScan} loading={loading || active} />

      <main className="mx-auto max-w-6xl px-6 pb-24 pt-8 md:px-10">
        {!scan && (
          <>
            <EmptyIntro />
            <RecentSearches items={recent} onPick={pickRecent} />
          </>
        )}

        {scan && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex-1">
                <ScanProgress
                  status={scan.status}
                  progress={scan.progress}
                  total={scan.total}
                  niche={scan.niche}
                  area={scan.area}
                />
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  data-testid="new-scan-btn"
                  onClick={resetScan}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 font-sans text-sm text-ink transition-colors hover:border-electric hover:text-electric"
                >
                  <ArrowClockwise size={14} weight="bold" />
                  New scan
                </button>
                <button
                  type="button"
                  data-testid="export-csv-btn"
                  disabled={!canExport}
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 rounded-md bg-electric px-3 py-2 font-sans text-sm font-semibold text-white transition-transform hover:-translate-y-[1px] hover:bg-electric-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Export CSV
                </button>
              </div>
            </div>

            {scan.error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 font-sans text-sm text-destructive">
                {scan.error}
              </div>
            )}

            <SummaryStats rows={rows} />

            <ResultsTable
              rows={rows}
              total={scan.total || 0}
              progress={scan.progress || 0}
              status={scan.status}
              leads={leads}
              onEditLead={({ business, index, lead }) =>
                setLeadTarget({ business, index, lead })
              }
              onDraft={({ business, index }) =>
                setDraftTarget({ business, index })
              }
            />

            <Caveat />

            <RecentSearches items={recent} onPick={pickRecent} />
          </div>
        )}
      </main>

      <OutreachDrawer
        open={!!draftTarget}
        onOpenChange={(v) => !v && setDraftTarget(null)}
        scanId={scanId}
        business={draftTarget?.business}
        businessIndex={draftTarget?.index}
      />

      <LeadNotesDialog
        open={!!leadTarget}
        onOpenChange={(v) => !v && setLeadTarget(null)}
        business={leadTarget?.business}
        currentLead={leadTarget?.lead}
        scanContext={{ niche: scan?.niche, area: scan?.area }}
        onSaved={(placeId, saved) => {
          setLeads((prev) => ({ ...prev, [placeId]: saved }));
          bumpCallbackAgenda();
        }}
      />
    </div>
  );
}

function EmptyIntro() {
  const steps = [
    { n: "01", t: "Enter niche + area", d: "Try 'dentists · Dandenong, VIC' or 'cafes · Brooklyn, NY'." },
    { n: "02", t: "We map their GBPs", d: "We pull up to 60 businesses in that suburb from Google Maps." },
    { n: "03", t: "Post activity scored", d: "Each business gets an Active / Low / Dormant / None badge." },
    { n: "04", t: "Export & outreach", d: "Sort by dormant, hit export CSV, and start pitching." },
  ];
  return (
    <section className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-4">
      {steps.map((s) => (
        <div
          key={s.n}
          className="rounded-lg border border-border bg-white p-5 transition-colors hover:border-electric"
        >
          <div className="font-mono text-[11px] uppercase tracking-wider text-electric">
            {s.n}
          </div>
          <div className="mt-2 font-display text-lg font-semibold text-ink">
            {s.t}
          </div>
          <div className="mt-1 font-sans text-sm text-muted-foreground">
            {s.d}
          </div>
        </div>
      ))}
    </section>
  );
}

function SummaryStats({ rows }) {
  const total = rows.length;
  const active = rows.filter((r) => r.activity_status === "active").length;
  const dormant = rows.filter((r) => r.activity_status === "dormant").length;
  const none = rows.filter((r) => r.activity_status === "none" || !r.activity_status).length;
  const thisMonth = rows.reduce((s, r) => s + (r.posts_this_month || 0), 0);
  const lastMonth = rows.reduce((s, r) => s + (r.posts_last_month || 0), 0);
  const postedThisMonth = rows.filter((r) => (r.posts_this_month || 0) > 0).length;

  const items = [
    { label: "Businesses", value: total, tone: "text-ink" },
    { label: "Posted this month", value: postedThisMonth, tone: "text-electric" },
    { label: "Posts this month", value: thisMonth, tone: "text-electric" },
    { label: "Posts last month", value: lastMonth, tone: "text-ink" },
    { label: "Active", value: active, tone: "text-[#0C6B2E]" },
    { label: "Dormant", value: dormant, tone: "text-[#C92A2A]" },
    { label: "No posts", value: none, tone: "text-[#495057]" },
  ];
  return (
    <div
      data-testid="summary-stats"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7"
    >
      {items.map((it) => (
        <div
          key={it.label}
          className="rounded-lg border border-border bg-white px-4 py-3"
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {it.label}
          </div>
          <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${it.tone}`}>
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Caveat() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2 font-sans text-xs text-muted-foreground">
      <Info size={14} weight="bold" className="mt-0.5 flex-shrink-0 text-electric" />
      <p>
        Post counts are a best-effort estimate from Google Business
        Profile&apos;s public updates section. We fetch page 1 of posts (~10
        most recent) per business — enough to identify current-month activity
        while keeping scans fast. A &ldquo;+&rdquo; next to a count
        means older posts exist that we intentionally skipped. Post dates are
        parsed from both relative and absolute formats.
      </p>
    </div>
  );
}
